import * as React from 'react'
import { useState } from 'react'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { Dropdown } from '@/ui/form/controls/Dropdown'
import { AdminTable } from '@/modules/adminPanel/shared/AdminTable'
import { FaIcon, faEnvelope, faTrash } from '@/lib/fa'
import AddUserColor from '@/assets/icons_new/AddUserColor.svg'
import CloseGray from '@/assets/icons_new/CloseGray.svg'
import { toast } from '@/lib/toasts/api'
import { useMutationSendInvitation } from '@/lib/api/company/configuration/adminPanel/users'
import {
  ServiceAccount,
  ServiceAccountUser,
  UserSearchResult,
  useMutationInviteServiceAccountUser,
  useMutationRemoveServiceAccountUser,
  useMutationSetServiceAccountUser,
  useQueryServiceAccountUsers,
  useQueryUserSearch,
} from '@/modules/shared/query-client/useServiceAccountAdmin'

// Role picker options — the roster's only editable field. 'owner' ⇒ isOwner true.
const ROLE_OPTIONS = [
  { label: 'Właściciel', value: 'owner' },
  { label: 'Członek', value: 'member' },
]

export const extractCode = (e: unknown): string | undefined => (e as any)?.response?.data?.code

// 0 = Manual (panel/NIP), 1 = Stripe (legacy), 2 = Przelewy24.
export const billingProviderLabel = (p: number): string =>
  p === 0 ? 'Ręczne (panel / NIP)' : p === 1 ? 'Stripe' : p === 2 ? 'Przelewy24' : '—'

export const rosterErrorMessage = (code: string | undefined): string => {
  switch (code) {
    case 'NOT_AUTHORIZED':
      return 'Brak uprawnień do tej operacji.'
    case 'LAST_OWNER':
      return 'To jedyny właściciel konta. Najpierw wskaż innego właściciela.'
    case 'NOT_SERVICE_COMPANY':
      return 'Ta firma nie jest kontem usługowym.'
    case 'USER_NOT_FOUND':
      return 'Nie znaleziono użytkownika.'
    case 'LOGIN_REQUIRED':
      return 'Podaj adres e-mail użytkownika.'
    case 'INVITE_FAILED':
      return 'Nie udało się utworzyć konta użytkownika. Sprawdź adres e-mail i spróbuj ponownie.'
    default:
      return 'Operacja nie powiodła się.'
  }
}

export const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <th className={`tw-text-left tw-px-3 tw-py-2 tw-font-semibold ${className ?? ''}`}>{children}</th>
)

export const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <td className={`tw-px-3 tw-py-2 tw-align-top ${className ?? ''}`}>{children}</td>

const RoleBadge: React.FC<{ isOwner: boolean }> = ({ isOwner }) => (
  <span
    className={`tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${
      isOwner ? 'tw-bg-blue-100 tw-text-blue-800' : 'tw-bg-gray-100 tw-text-gray-700'
    }`}
  >
    {isOwner ? 'Właściciel' : 'Członek'}
  </span>
)

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString('pl-PL') : '—'

export const ServiceAccountUsersPanel: React.FC<{
  account: ServiceAccount
  onError: (message: string | null) => void
  /** Members see the list only — no owner-toggle / remove / invite. */
  readOnly?: boolean
  /** Expose the existing-user typeahead (global-admin panel only). */
  allowSearch?: boolean
}> = (props) => (
  // Selection context powers the checkbox column + the "selected" bulk action bar, mirroring the
  // users module (usersModule) — the same TableProvider it uses.
  <TableProvider>
    <RosterPanel {...props} />
  </TableProvider>
)

const RosterPanel: React.FC<{
  account: ServiceAccount
  onError: (message: string | null) => void
  readOnly?: boolean
  allowSearch?: boolean
}> = ({ account, onError, readOnly = false, allowSearch = false }) => {
  const { data, isLoading, error } = useQueryServiceAccountUsers(account.id)
  const setUser = useMutationSetServiceAccountUser(account.id)
  const { isSelectedAny } = useTable()
  const [showInvite, setShowInvite] = useState(false)

  const users = data ?? []
  const busy = setUser.isPending

  const onSetOwner = (u: ServiceAccountUser, isOwner: boolean) => {
    if (isOwner === u.isOwner) return
    onError(null)
    setUser.mutate(
      { userId: u.userId, isOwner },
      { onError: (e) => onError(rosterErrorMessage(extractCode(e))) },
    )
  }

  if (error) {
    const status = (error as any)?.response?.status
    return (
      <div className="tw-p-4 tw-text-sm tw-text-red-600">
        {status === 403
          ? 'Brak uprawnień do zarządzania użytkownikami tego konta.'
          : 'Nie udało się załadować użytkowników.'}
      </div>
    )
  }

  return (
    <div>
      <p className="tw-px-4 tw-pt-4 tw-pb-3 tw-text-sm tw-text-gray-600">
        Lista obejmuje wyłącznie użytkowników <strong>całego konta</strong> ({users.length}). Mają
        oni dostęp do <strong>wszystkich firm</strong> w koncie, również do dodanych później.
        Właściciel może dodatkowo zarządzać tą listą oraz użytkownikami w firmach. Osoby dodane do
        pojedynczej firmy nie pojawiają się tutaj. Zarządza się nimi w zakładce „Użytkownicy" tej
        firmy.
      </p>

      {!readOnly && (
        <div className="tw-relative">
          {isSelectedAny ? (
            <SelectedActionBar account={account} users={users} onError={onError} />
          ) : (
            <BasicActionBar onAdd={() => setShowInvite((v) => !v)} active={showInvite} />
          )}
        </div>
      )}

      {!readOnly && showInvite && !isSelectedAny && (
        <div className="tw-px-4 tw-py-3">
          <InviteUserControl account={account} onError={onError} allowSearch={allowSearch} />
        </div>
      )}

      <AdminTable
        idPrefix="roster"
        rows={users}
        rowId={(u) => u.userId}
        selectable={!readOnly}
        isLoading={isLoading}
        columns={[
          {
            key: 'login',
            header: 'Użytkownik',
            render: (u) => {
              const name = [u.firstName, u.surname].filter(Boolean).join(' ')
              return (
                <div>
                  <div className="tw-font-medium">{u.login}</div>
                  {name && <div className="tw-text-xs tw-text-content-base tw-opacity-60">{name}</div>}
                </div>
              )
            },
          },
          {
            // Skąd się tu wziął ten ktoś: data dopisania do konta + ostatnie
            // logowanie. Bez tego nierozpoznany login na liście nic nie mówi.
            key: 'created',
            header: 'W koncie od',
            width: 200,
            render: (u) => (
              <div>
                <div>{formatDate(u.created)}</div>
                {u.lastLogin && (
                  <div className="tw-text-xs tw-text-content-base tw-opacity-60">
                    ost. logowanie: {formatDate(u.lastLogin)}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'role',
            header: 'Rola',
            width: 220,
            render: (u) =>
              readOnly ? (
                <RoleBadge isOwner={u.isOwner} />
              ) : (
                <Dropdown
                  compact
                  portalMenu
                  options={ROLE_OPTIONS}
                  value={u.isOwner ? 'owner' : 'member'}
                  disabled={busy}
                  onChange={(value: string) => onSetOwner(u, value === 'owner')}
                />
              ),
          },
        ]}
      />
    </div>
  )
}

// Default action bar (nothing selected) — mirrors usersModule's UsersBasicActionBar.
const BasicActionBar: React.FC<{ onAdd: () => void; active: boolean }> = ({ onAdd, active }) => {
  const { t } = useLocalize()
  return (
    <div className="tw-flex tw-items-center tw-justify-start tw-gap-2 tw-border-b tw-border-gray-200 tw-bg-content-primary tw-px-4 tw-py-1.5">
      <div
        onClick={onAdd}
        className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90 ${
          active ? 'tw-bg-base-300' : ''
        }`}
      >
        <img src={AddUserColor} alt="" />
        <p className="tw-text-sm tw-font-medium tw-text-primary">{t('usersModule.addUser')}</p>
      </div>
    </div>
  )
}

// Bulk action bar shown while rows are selected — mirrors usersModule's UsersSelectedActionBar
// (resend invitation + delete), wired to the service-account endpoints.
const SelectedActionBar: React.FC<{
  account: ServiceAccount
  users: ServiceAccountUser[]
  onError: (message: string | null) => void
}> = ({ account, users, onError }) => {
  const { t } = useLocalize()
  const { selectedGroup, resetAll } = useTable()
  const confirm = useConfirm()
  const resend = useMutationSendInvitation(account.id)
  const removeUser = useMutationRemoveServiceAccountUser(account.id)
  const [isDeleting, setIsDeleting] = useState(false)

  const ids = selectedGroup?.ids ?? []
  const selectedCount = ids.length
  const selectedLogins = users.filter((u) => ids.includes(u.userId)).map((u) => u.login)

  const handleResend = async () => {
    if (selectedLogins.length === 0 || resend.isPending) return
    onError(null)
    try {
      await Promise.all(selectedLogins.map((login) => resend.mutateAsync(login)))
      resetAll()
      toast.success({ text: t('usersModule.invitationSent') })
    } catch {
      toast.error({ text: t('usersModule.invitationError') })
    }
  }

  const handleDelete = async () => {
    if (ids.length === 0 || isDeleting) return
    const ok = await confirm({
      title: 'Usunąć użytkowników z konta?',
      message: `Zaznaczeni użytkownicy stracą dostęp do wszystkich firm w koncie „${account.companyName}". Ich konta pozostaną bez zmian.`,
      confirmText: 'Usuń',
      cancelText: 'Anuluj',
    })
    if (!ok) return
    setIsDeleting(true)
    onError(null)
    try {
      await Promise.all(ids.map((id) => removeUser.mutateAsync({ userId: Number(id) })))
      resetAll()
    } catch (e) {
      onError(rosterErrorMessage(extractCode(e)))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="tw-relative tw-z-20 tw-flex tw-w-full tw-justify-between tw-border-b-2 tw-border-primary tw-bg-[#F1F4FF] tw-px-4 tw-py-3.5 lg:tw-items-center">
      <div className="tw-flex tw-flex-1 tw-items-center tw-gap-5">
        <p className="tw-space-x-2 tw-text-sm">
          <span className="tw-inline-block">{t('common.selected')}</span>
          <span className="tw-font-medium">{selectedCount}</span>
        </p>

        <div className="tw-flex tw-items-center tw-gap-4">
          <Tooltip
            text={resend.isPending ? t('usersModule.sending') : t('usersModule.resendInvitation')}
            onClick={resend.isPending ? undefined : handleResend}
          >
            <div
              className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 ${
                resend.isPending
                  ? 'tw-cursor-not-allowed tw-opacity-50'
                  : 'tw-cursor-pointer hover:tw-bg-[#e5e6e6]'
              }`}
            >
              <FaIcon icon={faEnvelope} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
            </div>
          </Tooltip>

          <Tooltip text={t('common.delete')} onClick={isDeleting ? undefined : handleDelete}>
            <div
              className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 ${
                isDeleting
                  ? 'tw-cursor-not-allowed tw-opacity-50'
                  : 'tw-cursor-pointer hover:tw-bg-[#e5e6e6]'
              }`}
            >
              <FaIcon icon={faTrash} className="tw-h-4 tw-w-4 tw-text-red-500" />
            </div>
          </Tooltip>
        </div>
      </div>

      <Tooltip text={t('common.close')} onClick={() => resetAll()}>
        <div className="tw-relative tw-cursor-pointer">
          <img src={CloseGray} alt="" />
        </div>
      </Tooltip>
    </div>
  )
}

const InviteUserControl: React.FC<{
  account: ServiceAccount
  onError: (message: string | null) => void
  allowSearch: boolean
}> = ({ account, onError, allowSearch }) => {
  const [login, setLogin] = useState('')
  const [isOwner, setIsOwner] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const invite = useMutationInviteServiceAccountUser(account.id)
  const setUser = useMutationSetServiceAccountUser(account.id)

  // Only fetched when the typeahead is enabled (admin panel). Owners add by e-mail, so the whole
  // user base is never queried on their behalf.
  const { data: matches, isFetching } = useQueryUserSearch(allowSearch ? login : '')

  const reset = () => {
    setLogin('')
    setIsOwner(false)
    setShowResults(false)
  }

  // Invite by e-mail — provisions a Keycloak account for a brand-new person, or simply adds an
  // existing one, then adds them to the account.
  const onInvite = () => {
    const value = login.trim()
    if (!value) return
    onError(null)
    invite.mutate(
      { login: value, isOwner },
      { onSuccess: reset, onError: (e) => onError(rosterErrorMessage(extractCode(e))) },
    )
  }

  // Add an existing user picked from the search — no e-mail round-trip needed.
  const onPick = (u: UserSearchResult) => {
    onError(null)
    setUser.mutate(
      { userId: u.id, isOwner },
      { onSuccess: reset, onError: (e) => onError(rosterErrorMessage(extractCode(e))) },
    )
  }

  const busy = invite.isPending || setUser.isPending
  const results = matches ?? []

  return (
    <div className="tw-rounded tw-border tw-border-gray-200 tw-p-4">
      <div className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-800">Dodaj użytkownika</div>
      <p className="tw-mb-3 tw-text-xs tw-text-gray-500">
        {allowSearch
          ? 'Wyszukaj istniejącego użytkownika po nazwisku lub e-mailu i wybierz go z listy. Możesz też podać pełny adres e-mail, aby zaprosić kogoś, kto nie ma jeszcze konta (otrzyma link do ustawienia hasła).'
          : 'Podaj adres e-mail. Jeśli użytkownik nie ma jeszcze konta, otrzyma e-mail z linkiem do ustawienia hasła. Istniejący użytkownik zostanie po prostu dodany do konta.'}
      </p>
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-relative tw-flex-1">
          <input
            type={allowSearch ? 'text' : 'email'}
            value={login}
            onChange={(e) => {
              setLogin(e.target.value)
              if (allowSearch) setShowResults(true)
            }}
            onFocus={() => allowSearch && setShowResults(true)}
            onKeyDown={(e) => e.key === 'Enter' && onInvite()}
            placeholder={
              allowSearch
                ? 'szukaj użytkownika lub podaj adres e-mail…'
                : 'adres e-mail użytkownika'
            }
            className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          />
          {allowSearch && showResults && login.trim().length >= 2 && (
            <div className="tw-absolute tw-z-10 tw-mt-1 tw-w-full tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg tw-max-h-64 tw-overflow-y-auto tw-divide-y tw-divide-gray-100">
              {isFetching && (
                <div className="tw-py-3 tw-text-center tw-text-gray-500 tw-text-sm">Szukanie…</div>
              )}
              {!isFetching && results.length === 0 && (
                <div className="tw-py-3 tw-px-3 tw-text-gray-500 tw-text-sm">
                  Brak pasujących użytkowników. Użyj „Zaproś", aby dodać nowy adres e-mail.
                </div>
              )}
              {!isFetching &&
                results.map((u) => {
                  const name = [u.firstName, u.surname].filter(Boolean).join(' ')
                  return (
                    <button
                      key={u.id}
                      onClick={() => onPick(u)}
                      disabled={busy}
                      className="tw-block tw-w-full tw-text-left tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-50 disabled:tw-opacity-50"
                    >
                      <div className="tw-text-sm tw-text-gray-900">{u.login}</div>
                      {name && <div className="tw-text-xs tw-text-gray-500">{name}</div>}
                    </button>
                  )
                })}
            </div>
          )}
        </div>
        <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700 tw-cursor-pointer">
          <input
            type="checkbox"
            checked={isOwner}
            onChange={(e) => setIsOwner(e.target.checked)}
            className="tw-cursor-pointer"
          />
          Właściciel
        </label>
        <button
          onClick={onInvite}
          disabled={busy || !login.trim()}
          className="tw-rounded tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-emerald-600 hover:tw-bg-emerald-700 disabled:tw-opacity-50"
        >
          {invite.isPending ? 'Wysyłanie…' : 'Zaproś'}
        </button>
      </div>
    </div>
  )
}
